import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Building2, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import SEOHead from '../components/SEOHead';

const tabs = [
  { id: 'nybyg', label: 'Nybyg', icon: Building2 },
  { id: 'renovering', label: 'Renovering', icon: RefreshCw },
];

const content = {
  nybyg: {
    subtitle: 'Elinstallationer til erhvervsbyggeri',
    title: 'Nybyg',
    description: 'Vi leverer komplette elinstallationer til nybyggeri med fokus på kvalitet, koordinering og driftssikre løsninger. Hos Dilling EL arbejder vi tæt sammen med bygherre, entreprenører og rådgivere for at sikre et effektivt projektforløb fra tilbud til aflevering.',
    image: '/images/film-grabs/still-10.jpg',
    benefits: [
      'Fast projektleder gennem hele projektet',
      'Tydelig koordinering med øvrige faggrupper',
      'Driftssikre og fremtidssikrede installationer',
      'Faste montørteams og klare tidsplaner',
      'Dokumentation og kvalitetssikring gennem hele byggeriet',
    ],
    process: [
      { step: '01', title: 'Gennemgang af projekt', desc: 'Vi gennemgår projektmateriale, ønsker og tekniske krav for at sikre det rette grundlag for installationerne.' },
      { step: '02', title: 'Tilbud & projektering', desc: 'Vi udarbejder et gennemsigtigt tilbud og planlægger installationerne i tæt koordinering med byggeriets øvrige parter.' },
      { step: '03', title: 'Installation', desc: 'Elinstallationerne udføres af faste teams med fokus på kvalitet, fremdrift og overholdelse af tidsplaner.' },
      { step: '04', title: 'Aflevering & dokumentation', desc: 'Projektet gennemgås og afleveres med nødvendig dokumentation, så installationerne er klar til drift og fremtidig service.' },
    ],
  },
  renovering: {
    subtitle: 'Renovering og modernisering',
    title: 'Renovering',
    description: 'Vi hjælper virksomheder med renovering og modernisering af elinstallationer i eksisterende bygninger. Arbejdet planlægges med fokus på sikker drift, tydelig koordinering og løsninger der passer til bygningens nuværende og fremtidige behov.',
    image: '/images/film-grabs/still-11.jpg',
    benefits: [
      'Fast kontaktperson gennem hele projektet',
      'Gennemgang og vurdering af eksisterende installationer',
      'Opdatering til moderne og driftssikre løsninger',
      'Planlagt arbejde med mindst mulig driftsforstyrrelse',
      'Dokumentation og overblik før og efter renovering',
    ],
    process: [
      { step: '01', title: 'Tilstandsvurdering', desc: 'Vi gennemgår de eksisterende installationer og vurderer behov, kapacitet og muligheder for renovering eller opgradering.' },
      { step: '02', title: 'Plan & tilbud', desc: 'På baggrund af gennemgangen udarbejder vi et konkret forslag med løsning, tidsplan og tilbud.' },
      { step: '03', title: 'Udførelse', desc: 'Renoveringen udføres i planlagte etaper med fokus på sikkerhed, kvalitet og minimal påvirkning af den daglige drift.' },
      { step: '04', title: 'Gennemgang & dokumentation', desc: 'Den færdige installation gennemgås og dokumenteres, så I har overblik over løsning, ændringer og fremtidigt vedligehold.' },
    ],
  },
};

export default function NybygPage() {
  const [activeTab, setActiveTab] = useState('nybyg');
  const data = content[activeTab];

  return (
    <main data-testid="nybyg-page" className="pt-24">
      <SEOHead path="/nybyg-og-renovering" />
      {/* Hero with Tab Switcher */}
      <section className="py-24 md:py-32 bg-[#F4F2EE]">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24">
          {/* Tab Switcher */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex gap-2 mb-12"
          >
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-3 text-sm transition-all duration-300 ${
                    activeTab === tab.id
                      ? 'bg-[#0E3D24] text-white'
                      : 'bg-transparent border border-[#1E2A24]/20 text-[#1E2A24] hover:border-[#0E3D24]'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <Icon size={16} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="text-[11px] tracking-[0.2em] uppercase text-[#A6B8A2]">
                {data.subtitle}
              </span>
              <h1 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl lg:text-6xl font-medium text-[#0E3D24] mt-4 leading-[1.1]">
                {data.title}
              </h1>
              <p className="text-[#1E2A24]/70 text-lg mt-6 leading-relaxed max-w-xl">
                {data.description}
              </p>
              <Link 
                to="/kontakt"
                className="inline-flex items-center gap-3 mt-10 text-[#1F5A3A] hover:text-[#0E3D24] text-sm tracking-[0.1em] uppercase group transition-colors"
              >
                <span className="link-underline">Få et tilbud</span>
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>

            <motion.div
              key={`image-${activeTab}`}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  loading="lazy" 
                  src={data.image} 
                  alt={data.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits - Hvad I får */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-[11px] tracking-[0.2em] uppercase text-[#A6B8A2]">
                Fordele
              </span>
              <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl font-medium text-[#0E3D24] mt-6">
                Hvad I får
              </h2>
            </motion.div>

            <motion.div
              key={`benefits-${activeTab}`}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="space-y-4"
            >
              {data.benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-4">
                  <CheckCircle2 size={20} className="text-[#0E3D24] mt-0.5 flex-shrink-0" />
                  <span className="text-[#1E2A24]/70">{benefit}</span>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process - Sådan arbejder vi */}
      <section className="py-24 md:py-32 bg-[#F4F2EE]">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <span className="text-[11px] tracking-[0.2em] uppercase text-[#A6B8A2]">
              Proces
            </span>
            <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl font-medium text-[#0E3D24] mt-6">
              Sådan arbejder vi
            </h2>
          </motion.div>

          <motion.div
            key={`process-${activeTab}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {data.process.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <span className="text-[#A6B8A2] text-sm">{item.step}</span>
                <h3 className="font-['Cormorant_Garamond'] text-xl font-medium text-[#1E2A24] mt-2 mb-3">
                  {item.title}
                </h3>
                <p className="text-[#1E2A24]/70 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#0E3D24]">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-medium text-white mb-8">
              Klar til at komme i gang?
            </h2>
            <Link 
              to="/kontakt"
              className="inline-flex items-center gap-3 text-[11px] tracking-[0.15em] uppercase bg-white text-[#0E3D24] px-8 py-4 hover:bg-[#F4F2EE] transition-colors group"
            >
              <span>Kontakt os</span>
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
